import { useEffect, useLayoutEffect, useMemo } from 'react'
import * as THREE from 'three'
import { poseMotoristaImpactos, type PontoMotorista } from '../../dominio/didatico/poseMotoristaImpactos'
import type { OrgaoImpactos } from '../../dominio/didatico/quatroImpactos'
import { AnatomiaImpactos3D, CranioImpactos3D, EncefaloImpactos3D, EsqueletoImpactos3D } from './AnatomiaImpactos3D'

function Membro({ de, para, raio, raioX, osso = false }: { de: PontoMotorista; para: PontoMotorista; raio: number; raioX: boolean; osso?: boolean }) {
  const a = new THREE.Vector3(...de), b = new THREE.Vector3(...para)
  return <mesh position={a.clone().add(b).multiplyScalar(0.5)} quaternion={new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize())}>
    <capsuleGeometry args={[raio, Math.max(0.005, a.distanceTo(b) - raio * 2), 6, 16]} />
    <meshStandardMaterial color={osso ? '#e0daca' : raioX ? '#bdd1e0' : '#c9a88e'} transparent={raioX && !osso} opacity={raioX && !osso ? 0.25 : 1} depthWrite={!raioX || osso} roughness={0.6} />
  </mesh>
}
function Volume({ posicao, escala, raioX, osso = false }: { posicao: PontoMotorista; escala: PontoMotorista; raioX: boolean; osso?: boolean }) {
  return <mesh position={posicao} scale={escala}><sphereGeometry args={[1, 24, 16]} />
    <meshStandardMaterial color={osso ? '#ded8c9' : raioX ? '#acd5ed' : '#c9a88e'} transparent={raioX && !osso} opacity={raioX && !osso ? 0.2 : 1} depthWrite={!raioX || osso} roughness={0.65} />
  </mesh>
}
function Tronco({ raioX, carga }: { raioX: boolean; carga: number }) {
  const geometria = useMemo(() => new THREE.SphereGeometry(1, 32, 24).scale(0.252, 0.45, 0.156).translate(0, 0.91, 0), [])
  const base = useMemo(() => new Float32Array(geometria.getAttribute('position').array), [geometria])
  useLayoutEffect(() => {
    const p = geometria.getAttribute('position')
    for (let i = 0; i < p.count; i++) {
      const x = base[i * 3], y = base[i * 3 + 1], z = base[i * 3 + 2]
      const faixa = 0.22 - (1.18 - y) * 0.8
      const contato = Math.exp(-Math.pow((x - faixa) / 0.035, 2)) * (y > 0.65 && y < 1.2 && z > 0 ? 1 : 0)
      p.setXYZ(i, x, y, z - contato * carga * 0.016)
    }
    p.needsUpdate = true; geometria.computeVertexNormals()
  }, [base, carga, geometria])
  useEffect(() => () => geometria.dispose(), [geometria])
  return <mesh name="tronco-compressao-didatica" geometry={geometria}><meshStandardMaterial color={raioX ? '#6fcfff' : '#c9a88e'} transparent={raioX} opacity={raioX ? 0.075 : 1} depthWrite={!raioX} roughness={0.7} /></mesh>
}
function Faixa({ pontos, cor, largura = 0.043, ajustar }: { pontos: PontoMotorista[]; cor: string; largura?: number; ajustar?: (p: PontoMotorista) => PontoMotorista }) {
  const coordenadas = JSON.stringify(pontos)
  const geometria = useMemo(() => {
    const curva = new THREE.CatmullRomCurve3(pontos.map(p => new THREE.Vector3(...p)))
    const vertices: number[] = [], indices: number[] = []
    for (let i = 0; i <= 32; i++) {
      const p = curva.getPoint(i / 32), t = curva.getTangent(i / 32)
      const lado = new THREE.Vector3(-t.y, t.x, 0).normalize().multiplyScalar(largura / 2)
      for (const sinal of [-1, 1]) {
        const ponto: PontoMotorista = [p.x + lado.x * sinal, p.y + lado.y * sinal, p.z]
        vertices.push(...(ajustar ? ajustar(ponto) : ponto))
      }
      if (i < 32) { const n = i * 2; indices.push(n, n + 1, n + 2, n + 1, n + 3, n + 2) }
    }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3)); g.setIndex(indices); g.computeVertexNormals(); return g
  }, [coordenadas, largura, ajustar])
  useEffect(() => () => geometria.dispose(), [geometria])
  return <mesh geometry={geometria}><meshStandardMaterial color={cor} side={THREE.DoubleSide} roughness={0.8} /></mesh>
}
function Mao({ posicao, raioX }: { posicao: PontoMotorista; raioX: boolean }) {
  return <group name="mao-segurando-volante" position={posicao}>
    <Volume posicao={[0, -0.018, -0.018]} escala={[0.031, 0.045, 0.025]} raioX={raioX} />
    {[-0.025, -0.007, 0.011, 0.027].map(y => <mesh key={y} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}>
      <torusGeometry args={[0.018, 0.006, 6, 16, Math.PI * 1.65]} /><meshStandardMaterial color="#c9a88e" roughness={0.7} />
    </mesh>)}
    <Volume posicao={[0.02, -0.006, -0.012]} escala={[0.013, 0.029, 0.012]} raioX={false} />
  </group>
}
function Calcado({ x }: { x: number }) {
  return <group name="pe-com-calcado" position={[x, 0.285, 0.68]} rotation={[-0.12, 0, 0]}>
    <mesh scale={[0.062, 0.04, 0.12]}><sphereGeometry args={[1, 24, 16]} /><meshStandardMaterial color="#35485b" roughness={0.85} /></mesh>
    <mesh position={[0, -0.025, 0.01]} scale={[0.064, 0.018, 0.12]}><sphereGeometry args={[1, 20, 12]} /><meshStandardMaterial color="#8294a5" roughness={0.95} /></mesh>
    {[0, 1, 2].map(i => <mesh key={i} position={[0, 0.03, -0.035 + i * 0.023]}><boxGeometry args={[0.07, 0.004, 0.006]} /><meshStandardMaterial color="#b6c6d0" /></mesh>)}
  </group>
}
export function MotoristaImpactos3D({ carga, raioX, deslocamentoOrgaos, foco, aoSelecionar, destacarOrgaos, destacarCinto, mostrarCinto = true, anguloPescoco = 0 }: {
  carga: number; raioX: boolean; deslocamentoOrgaos: number; foco: OrgaoImpactos | null
  aoSelecionar: (id: OrgaoImpactos) => void; destacarOrgaos: boolean; destacarCinto: boolean; mostrarCinto?: boolean
  /** flexão do pescoço em relação ao tronco, em radianos (positivo para a frente) */
  anguloPescoco?: number
}) {
  const pose = poseMotoristaImpactos(carga), T = pose.transformarTronco
  const corCinto = destacarCinto ? '#f9b454' : '#9cb3c6'
  const contato = (x: number, y: number): PontoMotorista => T([x, y, 0.156 * Math.sqrt(Math.max(0.03, 1 - (x / 0.252) ** 2 - ((y - 0.91) / 0.45) ** 2)) - pose.carga * 0.016 + 0.006])
  const ajustarFaixa = useMemo(() => ([x, y, z]: PontoMotorista): PontoMotorista => {
    const a = pose.anguloTronco
    const localY = 0.54 + (y - 0.54) * Math.cos(a) + z * Math.sin(a)
    const localZ = -(y - 0.54) * Math.sin(a) + z * Math.cos(a)
    const elipse = 1 - (x / 0.252) ** 2 - ((localY - 0.91) / 0.45) ** 2
    if (localY < 0.65 || localY > 1.17 || localZ < 0 || elipse <= 0) return [x, y, z]
    const centroFaixa = 0.22 - (1.18 - localY) * 0.8
    const indentacao = Math.exp(-Math.pow((x - centroFaixa) / 0.035, 2)) * pose.carga * 0.016
    return T([x, localY, 0.156 * Math.sqrt(elipse) - indentacao + 0.006])
  }, [pose.carga])
  return <group name="motorista-postura-impactos">
    <group position={[0, 0.54, 0]} rotation={[pose.anguloTronco, 0, 0]}><group position={[0, -0.54, 0]}>
      <Tronco raioX={raioX} carga={pose.carga} />
      {/* Pescoço em duas metades: a base gira metade do ângulo e a cabeça, o ângulo todo. */}
      <group name="pescoco-cabeca" position={[0, 1.24, 0]} rotation={[anguloPescoco / 2, 0, 0]}>
        <Membro de={[0, 0, 0]} para={[0, 0.1, 0]} raio={0.045} raioX={raioX} />
        <group position={[0, 0.1, 0]} rotation={[anguloPescoco / 2, 0, 0]}><group position={[0, -1.34, 0]}>
          <Volume posicao={[0, 1.48, 0]} escala={[0.158, 0.19, 0.158]} raioX={raioX} />
          {raioX && <><CranioImpactos3D /><EncefaloImpactos3D deslocamento={deslocamentoOrgaos} foco={foco} aoSelecionar={aoSelecionar} destacar={destacarOrgaos} /></>}
        </group></group>
      </group>
      {raioX && <><EsqueletoImpactos3D /><AnatomiaImpactos3D deslocamento={deslocamentoOrgaos} foco={foco} aoSelecionar={aoSelecionar} destacar={destacarOrgaos} /></>}
    </group></group>
    <Volume posicao={[0, 0.53, 0]} escala={[0.20, 0.085, 0.13]} raioX={raioX} />
    {pose.membros.map(m => <group key={m.lado}>
      <Membro de={m.ombro} para={m.cotovelo} raio={0.055} raioX={raioX} />
      <Membro de={m.cotovelo} para={m.mao} raio={0.041} raioX={raioX} />
      <Mao posicao={m.mao} raioX={raioX} />
      <Membro de={m.quadril} para={m.joelho} raio={0.077} raioX={raioX} />
      <Volume posicao={m.joelho} escala={[0.066, 0.058, 0.059]} raioX={raioX} />
      <Membro de={m.joelho} para={m.tornozelo} raio={0.052} raioX={raioX} />
      <Calcado x={m.tornozelo[0]} />
      {raioX && <>
        <Membro de={m.quadril} para={m.joelho} raio={0.022} raioX osso />
        <Membro de={m.joelho} para={m.tornozelo} raio={0.015} raioX osso />
        <Membro de={[m.joelho[0] + m.lado * 0.027, m.joelho[1] - 0.025, m.joelho[2]]} para={[m.tornozelo[0] + m.lado * 0.023, m.tornozelo[1], m.tornozelo[2]]} raio={0.009} raioX osso />
        <Volume posicao={[m.joelho[0], m.joelho[1], m.joelho[2] + 0.045]} escala={[0.023, 0.028, 0.012]} raioX osso />
      </>}
    </group>)}
    {mostrarCinto && <group name="cinto-ancorado-carro">
      <mesh position={[0.50, 1.24, -0.31]} rotation={[0, Math.PI / 2, 0]}><torusGeometry args={[0.024, 0.006, 6, 16]} /><meshStandardMaterial color="#cad6df" metalness={0.7} /></mesh>
      <mesh position={[0.50, 0.37, -0.10]}><boxGeometry args={[0.03, 0.035, 0.07]} /><meshStandardMaterial color="#647d91" /></mesh>
      <Faixa cor={corCinto} ajustar={ajustarFaixa} pontos={[[0.50, 1.24, -0.31], T([0.22, 1.18, 0.062]), contato(0.145, 1.08), contato(0.016, 0.94), contato(-0.135, 0.73), [-0.24, 0.55, 0.075]]} />
      <Faixa cor={corCinto} pontos={[[-0.24, 0.55, 0.075], [-0.14, 0.545, 0.10], [0, 0.55, 0.137], [0.14, 0.545, 0.10], [0.50, 0.37, -0.10]]} />
      <mesh position={[-0.24, 0.55, 0.075]}><boxGeometry args={[0.044, 0.062, 0.027]} /><meshStandardMaterial color="#526779" /></mesh>
      <mesh position={[-0.24, 0.572, 0.091]}><boxGeometry args={[0.024, 0.009, 0.004]} /><meshStandardMaterial color="#e35c5c" /></mesh>
    </group>}
  </group>
}
